const MAX_IMAGE_WIDTH = 1400;
const IMAGE_OUTPUT_FORMAT = 'image/jpeg';
const IMAGE_QUALITY: Number = 0.8;

const resizeMax = ({
  width,
  height,
  maxWidth,
  maxHeight,
}: {
  width: number;
  height: number;
  maxWidth: number;
  maxHeight?: number;
}) => {
  let w = width;
  let h = height;
  let mw = maxWidth;
  let mh = maxHeight || mw;

  if (w > mw || h > mh) {
    if (mw / w < mh / h) {
      h = (mw * h) / w;
      w = mw;
    } else if (mh / h < mw / w) {
      w = (mh * w) / h;
      h = mh;
    } else {
      w = mw;
      h = mh;
    }
  }

  return [Math.round(w), Math.round(h)];
};

const cropImage = ({
  image,
  cropData,
  callback,
}: {
  image: string;
  cropData: {
    cropInfo: { sx: number; sy: number; sWidth: number; sHeight: number; rotation: number };
  };
  callback: (img: Blob | null) => void;
}) => {
  const img = new Image();
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  img.addEventListener('load', () => {
    const { sx, sy, sWidth, sHeight, rotation } = cropData.cropInfo;
    const [destWidth, destHeight] =
      sWidth > MAX_IMAGE_WIDTH
        ? resizeMax({ width: sWidth, height: sHeight, maxWidth: MAX_IMAGE_WIDTH })
        : [sWidth, sHeight];

    const destX = -destWidth / 2;
    const destY = -destHeight / 2;
    if (ctx) {
      if ((rotation / 90) % 2 === 0) {
        ctx.canvas.width = destWidth;
        ctx.canvas.height = destHeight;
      } else {
        ctx.canvas.width = destHeight;
        ctx.canvas.height = destWidth;
      }

      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

      if (rotation % 180 === 0) {
        ctx.translate(destWidth / 2, destHeight / 2);
      } else {
        ctx.translate(destHeight / 2, destWidth / 2);
      }

      if (rotation) {
        ctx.rotate((rotation * Math.PI) / 180);
      }
      ctx.drawImage(img, sx, sy, sWidth, sHeight, destX, destY, destWidth, destHeight);
      canvas.toBlob(callback, IMAGE_OUTPUT_FORMAT, IMAGE_QUALITY);
    }
  });

  img.src = image;
};

const resamplePhoto = ({
  canvas,
  widthProp,
  heightProp,
  resizeCanvas,
}: {
  canvas: { width: number; height: number; getContext: (contextType: string) => any };
  widthProp: number;
  heightProp: number;
  resizeCanvas: any;
}) => {
  let width = Math.round(widthProp);
  let height = Math.round(heightProp);
  const sourceWidth = canvas.width;
  const sourceHeight = canvas.height;
  const widthRatio = sourceWidth / width;
  const heightRatio = sourceHeight / height;
  const widthRatioHalf = Math.ceil(widthRatio / 2);
  const heightRatioHalf = Math.ceil(heightRatio / 2);

  const ctx = canvas.getContext('2d');
  const img = ctx.getImageData(0, 0, sourceWidth, sourceHeight);
  const img2 = ctx.createImageData(width, height);
  const data = img.data;
  const data2 = img2.data;

  for (let j = 0; j < height; j++) {
    for (let i = 0; i < width; i++) {
      const x2 = (i + j * width) * 4;
      let weight = 0;
      let weights = 0;
      let weightsAlpha = 0;
      let gxR = 0;
      let gxG = 0;
      let gxB = 0;
      let gxA = 0;
      const centerY = (j + 0.5) * heightRatio;
      const yyStart = Math.floor(j * heightRatio);
      const yyStop = Math.ceil((j + 1) * heightRatio);

      for (let yy = yyStart; yy < yyStop; yy++) {
        const dy = Math.abs(centerY - (yy + 0.5)) / heightRatioHalf;
        const centerX = (i + 0.5) * widthRatio;
        const w0 = dy * dy; // pre-calc part of w
        const xxStart = Math.floor(i * widthRatio);
        const xxStop = Math.ceil((i + 1) * widthRatio);
        for (let xx = xxStart; xx < xxStop; xx++) {
          const dx = Math.abs(centerX - (xx + 0.5)) / widthRatioHalf;
          const w = Math.sqrt(w0 + dx * dx);
          if (w >= 1) {
            // pixel too far
            continue;
          }
          // filter
          weight = 2 * w * w * w - 3 * w * w + 1;
          const posX = 4 * (xx + yy * sourceWidth);
          // alpha
          gxA += weight * data[posX + 3];
          weightsAlpha += weight;
          // colors
          if (data[posX + 3] < 255) {
            weight = (weight * data[posX + 3]) / 250;
          }
          gxR += weight * data[posX];
          gxG += weight * data[posX + 1];
          gxB += weight * data[posX + 2];
          weights += weight;
        }
      }
      data2[x2] = gxR / weights;
      data2[x2 + 1] = gxG / weights;
      data2[x2 + 2] = gxB / weights;
      data2[x2 + 3] = gxA / weightsAlpha;
    }
  }

  if (resizeCanvas) {
    canvas.width = width;
    canvas.height = height;
  } else {
    ctx.clearRect(0, 0, sourceWidth, sourceHeight);
  }

  ctx.putImageData(img2, 0, 0);
};

export { MAX_IMAGE_WIDTH, cropImage, resamplePhoto, resizeMax };
